// Context globale per l'Anno Scolastico attivo.
// Persistito in localStorage (`hub_anno_attivo`). Cambio anno → broadcasted a
// tutti i consumer via React context, così le tabelle si ri-filtrano.
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "hub_anno_attivo";

// Anno scolastico corrente: da settembre-agosto → anno = year se mese >= 9, altrimenti year-1.
export function currentAnnoScolastico() {
  const d = new Date();
  return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
}

export function formatAnnoScolastico(anno) {
  if (anno == null) return "—";
  const nextYY = String((Number(anno) + 1) % 100).padStart(2, "0");
  return `${anno}/${nextYY}`;
}

const AnnoContext = createContext({ anno: null, setAnno: () => {}, anniAvailable: [] });

export function AnnoScolasticoProvider({ children }) {
  const [anno, setAnnoState] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      const n = s ? Number(s) : NaN;
      return Number.isFinite(n) ? n : currentAnnoScolastico();
    } catch { return currentAnnoScolastico(); }
  });
  const [anniAvailable, setAnniAvailable] = useState([]);

  // Se l'utente non ha mai scelto un anno (nessun localStorage), adotta l'anno
  // corrente del backend (app_settings.anno_corrente): è la stessa fonte usata
  // dalle tabelle via AuthContext → selettore e dati restano allineati.
  useEffect(() => {
    (async () => {
      try {
        if (localStorage.getItem(STORAGE_KEY)) return;
        const { getAppSettings } = await import("@/lib/api");
        const r = await getAppSettings();
        const n = Number(r.data?.anno_corrente);
        if (Number.isFinite(n) && n > 2000) setAnnoState(n);
      } catch { /* non loggato o endpoint non disponibile: mantieni il default */ }
    })();
  }, []);

  const setAnno = useCallback((newAnno) => {
    const n = Number(newAnno);
    if (Number.isFinite(n)) {
      setAnnoState(n);
      try { localStorage.setItem(STORAGE_KEY, String(n)); } catch { /* ignore */ }
    }
  }, []);

  // Carica lista anni dal backend (endpoint anni disponibili). Genera fallback se fallisce.
  useEffect(() => {
    (async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const r = await api.get("/anni-disponibili");
        const list = Array.isArray(r.data?.anni) ? r.data.anni : [];
        if (list.length) {
          setAnniAvailable(list.sort((a, b) => b - a));
          return;
        }
      } catch { /* ignore */ }
      // Fallback: ultimi 5 anni scolastici
      const curr = currentAnnoScolastico();
      setAnniAvailable([curr + 1, curr, curr - 1, curr - 2, curr - 3, curr - 4]);
    })();
  }, []);

  return (
    <AnnoContext.Provider value={{ anno, setAnno, anniAvailable, formatAs: formatAnnoScolastico }}>
      {children}
    </AnnoContext.Provider>
  );
}

export function useAnnoScolastico() {
  return useContext(AnnoContext);
}
